<template>
  <div v-if="canEdit || canDelete">
    <div class="row">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.name") }}</label>
        <input
          type="text"
          v-model="data.name"
          :placeholder="$t('common.name')"
          :class="{ 'field-error': $v.data.name.$error }"
          class="form-control"
        />
      </div>
      <div class="form-group col-sm-6 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.surface_ha") }}</label>
        <input
          type="number"
          v-model="data.surface"
          :placeholder="$t('common.surface_ha')"
          :class="{ 'field-error': $v.data.surface.$error }"
          class="form-control"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-sm-6 col-lg-5 form-group text-left">
        <label class="control-label"> {{ $t("common.unit") }} </label>
        <DxSelectBox
          :value="data.department_uuid || null"
          :data-source="departments"
          display-expr="name"
          value-expr="uuid"
          :search-enabled="true"
          :class="{ 'field-error': $v.data.department_uuid.$error }"
          :placeholder="$t('common.unit')"
          @value-changed="onDepartmentSelected"
        />
      </div>
      <div class="col-sm-3 col-lg-3 form-group text-left">
        <label class="control-label"> {{ $t("common.season") }} </label>
        <DxSelectBox
          :value="data.season_uuid || null"
          :data-source="seasons"
          display-expr="name"
          value-expr="uuid"
          :search-enabled="true"
          :class="{ 'field-error': $v.data.season_uuid.$error }"
          :placeholder="$t('common.season')"
          :disabled="!data.is_deletable"
          @value-changed="onSeasonSelected"
        />
      </div>
      <div class="form-group col-sm-3 col-lg-3 text-left mt-3">
        <div class="upload-container d-flex align-items-center">
          <DxFileUploader
            :select-button-text="layerText"
            :multiple="false"
            label-text=""
            accept=".zip"
            upload-mode="useForm"
            @valueChanged="onFileChange"
            :input-attr="{ 'aria-label': 'Select Select Layer (zip)' }"
          />
          <i class="fa fa-trash-alt text-danger c-pointer" @click="removeLayer()" v-if="data.layer_uuid"></i>
        </div>
      </div>
    </div>
    <div class="row mt-1">
      <div class="col-sm-12 col-lg-5">
        <div class="form-group text-left mb-0">
          <label class="control-label">{{ $t("common.culture") }}</label>
          <DxSelectBox
            :value="data.culture_uuid || null"
            :data-source="cultures"
            display-expr="name"
            value-expr="uuid"
            :search-enabled="true"
            :placeholder="'-' + $t('common.culture') + '-'"
            @value-changed="onCultureSelected"
          />
        </div>
        <div class="text-primary fs11" v-html="$t('works.parcels.could_not_find', { parcel_uuid: data?.uuid })"></div>
      </div>
      <div class="col-sm-12 col-lg-5">
        <div class="form-group text-left mb-0">
          <label class="control-label">{{ $t("common.variety") }}</label>
          <DxSelectBox
            :value="data.variety_uuid || null"
            :data-source="varietyList"
            display-expr="name"
            value-expr="uuid"
            :search-enabled="true"
            :placeholder="'-' + $t('common.variety') + '-'"
            @value-changed="onVarietySelected"
          />
        </div>
      </div>
    </div>
     <div class="row mt-3">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.remote_id") }}</label>
        <input type="text" v-model="data.remote_id" :placeholder="$t('common.remote_id')" class="form-control" />
      </div>
            <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.settlement") }}</label>
        <input type="text" v-model="data.settlement" class="form-control" />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-5 text-left mb-0 pt-3">
        <Toggle
          :toggleText="$t('works.parcels.is_group')"
          :notToggledText="$t('common.no')"
          :toggledText="$t('common.yes')"
          :isActive="is_group"
          :onChange="onIsGroupChanged"
          :disabled="!data.is_deletable"
        >
        </Toggle>
      </div>
    </div>    
    <div class="row" v-if="!is_group">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.group") }}</label>
        <DxSelectBox
          :value="data.parcel_group?.uuid || null"
          :data-source="parcelGroups"
          display-expr="name"
          value-expr="uuid"
          :search-enabled="true"
          :placeholder="$t('works.parcels.select_group')"
          @value-changed="onParcelGroupSelected"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-5 text-left mb-0 pt-3">
        <label class="control-label mb-2">{{ $t("common.active") }}</label>
        <Toggle
          :is-active="isActive"
          :on-change="onIsActiveChanged"
          :has-toggle-text="false"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import required from "vuelidate/src/validators/required";
import { canDelete, canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import * as actions from "@/helpers/generalActions";
import DxSelectBox from "devextreme-vue/select-box";
import Toggle from "@/components/Toggle.vue";
import Forbidden from "@/pages/errors/Forbidden";
import { DxFileUploader } from "devextreme-vue/file-uploader";
import { readFile } from "@/helpers/generalActions";

export default {
  name: "ParcelDetailsContent",
  components: {
    DxSelectBox,
    Toggle,
    Forbidden,
    DxFileUploader,
  },
  props: {
    parcelData: {
      type: Object,
      default: () => ({}),
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    defaultNewValues: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      data: this.parcelData,
      is_group: false,
      uploadedFile: null,
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      parcelGroups: "works-parcels/parcelGroups",
      cultures: "works-cultures/cultures",
      varieties: "works-cultures/varieties",
      departments: "company/departments",
      domain: "auth/domain",
      seasons: "seasons/seasons",
    }),
    isActive() {
      return this.data?.is_active !== 0;
    },
    layerText() {
      if (this.data.layer_uuid) {
        return this.$t("works.parcels.change_layer") + " (zip)";
      }

      return this.$t("works.parcels.upload_layer") + " (zip)";
    },
    config() {
      return {
        permission: permissions.PARCELS,
        permission_type: permissionTypes.access,
        get: this.get,
        create: this.create,
        update: this.update,
        delete: this.delete,
        list_url: "/works/parcels",
        confirm_delete: true,
      };
    },
    actions() {
      return actions;
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this) && this.data.is_deletable;
    },
    varietyList() {
      if (this.varieties) {
        const varieties = this.varieties.map((item) => ({
          uuid: item.uuid,
          name: item.name,
        }));

        varieties.splice(0, 0, {
          uuid: null,
          name: "-" + this.$t("common.variety") + "-",
        });

        return varieties;
      }
      return [];
    },
  },
  methods: {
    ...mapActions({
      getCultures: "works-cultures/getCultures",
      getCultureVarieties: "works-cultures/getCultureVarieties",
      get: "works-parcels/getParcel",
      getParcelGroups: "works-parcels/getParcelGroups",
      create: "works-parcels/createParcel",
      update: "works-parcels/updateParcel",
      delete: "works-parcels/deleteParcel",
      getDepartments: "company/getDepartments",
      getSeasons: "seasons/getSeasons",
    }),
    loadData() {
      if (this.$route.params?.uuid && this.$route.params?.uuid !== "null") {
        return this.actions.getData(this, null, () => {
          this.is_group = this.data.is_group === 1;
          this.data.season = this.seasons.find((season) => season.uuid === this.data.season_uuid);
          this.data.culture = this.cultures.find((culture) => culture.uuid === this.data.culture_uuid);
          this.loadParcelGroups();
          this.loadVarieties(this.data.culture_uuid);
          this.$emit("title");
        });
      }
    },
    gotoList() {
      return actions.gotoList(this);
    },
    confirmDelete() {
      return this.actions.confirmDelete(this);
    },
    save() {
      this.data.shape_file = this.uploadedFile;
      return this.actions.save(this, this.isModal ? null : this.config.list_url);
    },
    saveAndNew() {
      return this.actions.saveAndNew(this, "add");
    },
    onSeasonSelected(e) {
      const selected = e?.component?.option("selectedItem") || null;
      const isUserAction = !!e?.event;
      this.data.season = selected;
      this.data.season_uuid = selected?.uuid ?? null;
      this.loadParcelGroups(isUserAction);
      this.$emit("input", this.data);
    },
    onDepartmentSelected(e) {
      const selected = e?.component?.option("selectedItem") || null;
      const isUserAction = !!e?.event;
      this.data.department = selected;
      this.data.department_uuid = selected?.uuid ?? null;
      this.loadParcelGroups(isUserAction);
      this.$emit("input", this.data);
    },
    onParcelGroupSelected(e) {
      const selected = e?.component?.option("selectedItem") || null;
      this.data.parcel_group_uuid = selected?.uuid ?? null;
      this.data.parcel_group = selected || null;
      this.$forceUpdate();
      this.$emit("input", this.data);
    },
    onIsGroupChanged(val) {
      this.data.is_group = val ? 1 : 0;
      this.is_group = val;

      if (val) {
        this.data.parcel_group_uuid = null;
        this.data.parcel_group = null;
      } else {
        this.loadParcelGroups();
      }
      this.$emit("input", this.data);
    },
    onIsActiveChanged(isActive) {
      this.data.is_active = isActive ? 1 : 0;
      this.$emit("input", this.data);
    },
    onCultureSelected(e) {
      const selected = e?.component?.option("selectedItem") || null;
      if (!selected?.uuid) {
        this.data.variety = null;
        this.data.variety_uuid = null;
      }

      if (selected) {
        this.data.culture = selected;
        this.data.culture_uuid = selected?.uuid ?? null;
      } else {
        this.data.culture = null;
        this.data.culture_uuid = null;
      }

      this.loadVarieties(this.data.culture_uuid);
      this.$forceUpdate();
      this.$emit("input", this.data);
    },
    onVarietySelected(e) {
      const selected = e?.component?.option("selectedItem") || null;
      if (selected) {
        this.data.variety = selected;
        this.data.variety_uuid = selected?.uuid ?? null;
      } else {
        this.data.variety = null;
        this.data.variety_uuid = null;
      }
      this.$forceUpdate();
      this.$emit("input", this.data);
    },
    loadParcelGroups(reset_group = false) {
      if (reset_group) {
        this.data.parcel_group_uuid = null;
        this.data.parcel_group = null;
      }

      if (this.data.department_uuid && this.data.season_uuid && !this.data.is_group) {
        this.getParcelGroups({
          department_uuid: this.data.department_uuid,
          season_uuid: this.data.season_uuid,
        });
      }
    },
    fillFieldsFromDefaultNewValues() {
      if (this.defaultNewValues && this.data?.uuid === "") {
        Object.keys(this.defaultNewValues).forEach((key) => {
          this.data[key] = this.defaultNewValues[key];
        });

        if (this.defaultNewValues.department_uuid) {
          this.data.department = this.departments.find((department) => department.uuid === this.defaultNewValues.department_uuid);
        }
        if (this.defaultNewValues.season_uuid) {
          this.data.season = this.seasons.find((season) => season.uuid === this.defaultNewValues.season_uuid);
        }
        if (this.defaultNewValues.culture) {
          this.data.culture = this.cultures.find((culture) => culture.name === this.defaultNewValues.culture);
          this.data.culture_uuid = this.data.culture?.uuid ?? null;
        }

        this.loadParcelGroups(true);
      }
    },
    removeLayer() {
      const vm = this;
      return this.actions.confirmDelete(this, this.$t("works.parcels.messages.confirm_delete_layer"), () => {
        vm.uploadedFile = null;
        vm.data.layer_uuid = null;
        vm.data.shape_file = null;
      });
    },
    onFileChange(e) {
      this.uploadedFile = readFile(e);
    },
    loadVarieties(culture_uuid) {
      const vm = this;

      if (!culture_uuid) {
        this.$store.commit("works-cultures/SET_VARIETIES", []);
        return;
      }

      this.getCultureVarieties({
        culture_uuid: culture_uuid,
      }).then((resp) => {
        if (this.data.variety_uuid) {
          const currentVariety = vm.varieties.find((variety) => variety.uuid === vm.data.variety_uuid);

          this.data.variety = {
            uuid: currentVariety?.uuid,
            name: currentVariety?.name,
          };
        }
      });
    },
  },
  mounted() {
    this.getDepartments({
      limit: 9999,
      company_uuid: this.domain?.uuid,
    });
    this.getCultures({
      enablePagination: 0,
      limit: 9999,
      is_variety: 0,
    });
    this.getSeasons({
      limit: 9999,
    });
    this.loadData();
    this.actions.handleParams(this);
    //document.querySelector('.b-form-file input').style.display = 'none';
  },
  watch: {
    data: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true,
    },
    $route: {
      handler: function (newVal) {
        this.actions.handleParams(this);
      },
      deep: true,
    },
    permission: {
      handler: function (newVal) {
        this.permissionModule = newVal;
      },
      deep: true,
    },
    defaultNewValues: {
      handler: function (newVal) {
        this.fillFieldsFromDefaultNewValues();
      },
      deep: true,
    },
  },
  validations() {
    return {
      data: {
        name: {
          required,
        },
        department_uuid: {
          required,
        },
        season_uuid: {
          required,
        },
        surface: {
          required,
        },
      },
    };
  },
};
</script>
